import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { FaThumbsUp } from 'react-icons/fa';
import DashboardReturnLink from './DashboardReturnLink';
import { DateTime } from 'luxon';
import { FiHeart, FiThumbsUp, FiSend, FiMessageCircle, FiX, FiExternalLink, FiSmartphone, FiArrowLeft, FiEdit2, FiTrash2 } from 'react-icons/fi';
import useSessionQna from './useSessionQna';
import { extractWeekNumber } from '../dashboard/activeAttendanceSessionsHelpers';
import { isInstructorReply, sessionQnaPath, openQnaPopup } from './qnaHelpers';
import StudentQnaPanel from './StudentQnaPanel';
import styles from './SessionQnaPanel.module.css';

function formatTime(value) {
  const dt = DateTime.fromJSDate(new Date(value)).setZone('America/Chicago');
  return dt.isValid ? dt.toFormat('h:mm a') : '';
}

// Renders plain text with http(s):// and www. URLs as clickable links. Builds
// React elements (never raw HTML), and only http/https hrefs are ever produced.
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"،؛؟]+$/;

function Linkified({ text }) {
  const parts = String(text).split(URL_PATTERN);
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    const trailing = (part.match(TRAILING_PUNCTUATION) || [''])[0];
    const url = trailing ? part.slice(0, -trailing.length) : part;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return (
      <React.Fragment key={i}>
        <a href={href} target="_blank" rel="noopener noreferrer nofollow" className={styles.messageLink}>{url}</a>
        {trailing}
      </React.Fragment>
    );
  });
}

export function ReactionButtons({ reactions, myReaction, disabled, onReact }) {
  return (
    <div className={styles.reactionRow}>
      <button
        type="button"
        className={`${styles.reactionButton} ${myReaction === 'like' ? `${styles.reactionButtonActive} ${styles.likeActive}` : ''}`}
        disabled={disabled}
        onClick={() => onReact('like')}
        aria-pressed={myReaction === 'like'}
      >
        {myReaction === 'like' ? <FaThumbsUp size={13} /> : <FiThumbsUp size={13} />}
        {reactions?.like || 0}
      </button>
      <button
        type="button"
        className={`${styles.reactionButton} ${myReaction === 'heart' ? `${styles.reactionButtonActive} ${styles.heartActive}` : ''}`}
        disabled={disabled}
        onClick={() => onReact('heart')}
        aria-pressed={myReaction === 'heart'}
      >
        <FiHeart size={13} />
        {reactions?.heart || 0}
      </button>
    </div>
  );
}

function ReplyComposer({ questionId, posting, onSubmit }) {
  const [value, setValue] = useState('');
  return (
    <form
      className={styles.replyComposer}
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim()) return;
        onSubmit(questionId, value);
        setValue('');
      }}
    >
      <input
        type="text"
        className={styles.replyInput}
        placeholder="Write a reply..."
        dir="auto"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
      />
      <button type="submit" className={styles.replySendButton} disabled={posting || !value.trim()}>
        <FiSend size={14} />
      </button>
    </form>
  );
}

// The app is served under the /portal router basename.
export { sessionQnaPath };

export function sessionQnaUrl(sessionId) {
  return `${window.location.origin}${sessionQnaPath(sessionId)}`;
}

export function QrPopover({ url, onHide }) {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 220, margin: 1, color: { dark: '#000000', light: '#F7F5F1' } })
      .then((d) => { if (!cancelled) setDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);
  return (
    <div className={styles.qrBox}>
      {dataUrl ? <img src={dataUrl} alt="QR code to open the Session Q&A on your phone" width={180} height={180} /> : null}
      <p className={styles.qrHint}>Scan QR code with your phone to open this Q&A session. You'll need to be signed in.</p>
      <a className={styles.qrLink} href={url} target="_blank" rel="noreferrer">{url}</a>
      <button type="button" className={styles.qrHideButton} onClick={onHide}>
        Hide QR Code
      </button>
    </div>
  );
}

// Moderation controls + inline editor for one message (instructor/admin only).
export function ModeratedBody({ message, targetType, canModerate, canEdit, onEdit, onDelete, children }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (editing) {
    return (
      <form
        className={styles.editForm}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!draft.trim()) return;
          setSaving(true);
          const ok = await onEdit(targetType, message.id, draft);
          setSaving(false);
          if (ok) setEditing(false);
        }}
      >
        <textarea
          className={styles.editInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={2000}
          dir="auto"
          rows={3}
          autoFocus
        />
        <div className={styles.editActions}>
          <button type="submit" className={styles.editSave} disabled={saving || !draft.trim()}>Save</button>
          <button type="button" className={styles.editCancel} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className={styles.messageBody} dir="auto">
        <Linkified text={message.body} />
        {message.editedAt && <span className={styles.editedTag}> (edited)</span>}
      </div>
      <div className={styles.actionRow}>
        {children}
        {(canModerate || canEdit) && (
          <div className={styles.moderationRow}>
            <button
              type="button"
              className={styles.moderationButton}
              onClick={() => { setDraft(message.body); setEditing(true); }}
              aria-label="Edit"
              title="Edit"
            >
              <FiEdit2 size={13} />
            </button>
            {canModerate && (
            <button
              type="button"
              className={`${styles.moderationButton} ${styles.moderationDelete}`}
              onClick={() => setConfirmingDelete(true)}
              aria-label="Delete"
              title="Delete"
            >
              <FiTrash2 size={13} />
            </button>
            )}
          </div>
        )}
      </div>
      {confirmingDelete && (
        <div className={styles.confirmOverlay} role="presentation" onClick={() => !deleting && setConfirmingDelete(false)}>
          <div
            className={styles.confirmDialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="qna-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="qna-delete-title" className={styles.confirmTitle}>
              Delete this {targetType === 'questions' ? 'question' : 'reply'}?
            </h4>
            <p className={styles.confirmText}>
              {targetType === 'questions'
                ? 'This will also delete all replies and reactions on it. This can\'t be undone.'
                : 'This can\'t be undone.'}
            </p>
            <div className={styles.confirmActions}>
              <button type="button" className={styles.editCancel} disabled={deleting} onClick={() => setConfirmingDelete(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDelete}
                disabled={deleting}
                autoFocus
                onClick={async () => {
                  setDeleting(true);
                  const ok = await onDelete(targetType, message.id);
                  setDeleting(false);
                  if (!ok) setConfirmingDelete(false);
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Inline dashboard card — not a modal. Matches the "My Quizzes / My Surveys /
// My Notes" summary-card convention (scrollable message list + a composer),
// so it drops directly into the page flow wherever it's mounted rather than
// popping up as an overlay.
function SessionQnaPanel({ open, onClose, sessionId, role, title, fullPage = false }) {
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const isInstructorRole = role === 'instructor';
  const showToast = useCallback((message) => {
    setToast(message);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 5000);
  }, []);
  // Live heads-ups that auto-dismiss: everyone hears about new questions from
  // others; students also hear about new instructor replies.
  const handleNewQuestion = useCallback((question) => {
    showToast(`New question from ${question.authorName}`);
  }, [showToast]);
  const handleNewReply = useCallback((reply) => {
    if (!isInstructorRole) showToast(`New reply from ${reply.authorName}`);
  }, [isInstructorRole, showToast]);
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  const { questions, isOpen, loading, error, posting, postQuestion, postReply, toggleReaction, sessionInfo, editMessage, deleteMessage } = useSessionQna(open ? sessionId : null, { onNewQuestion: handleNewQuestion, onNewReply: handleNewReply });
  const [questionDraft, setQuestionDraft] = useState('');
  const [askAnonymously, setAskAnonymously] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const isInstructor = role === 'instructor';

  if (!open) return null;

  const popOut = () => {
    if (openQnaPopup(sessionId) && onClose) onClose();
  };

  if (!isInstructor) {
    return (
      <StudentQnaPanel
        title={title}
        fullPage={fullPage}
        onClose={onClose}
        onPopOut={popOut}
        showQr={showQr}
        onToggleQr={() => setShowQr((v) => !v)}
        qrNode={<QrPopover url={sessionQnaUrl(sessionId)} onHide={() => setShowQr(false)} />}
        toast={toast}
        onDismissToast={() => setToast(null)}
        questions={questions}
        isOpen={isOpen}
        loading={loading}
        error={error}
        posting={posting}
        postQuestion={postQuestion}
        postReply={postReply}
        toggleReaction={toggleReaction}
        editMessage={editMessage}
        deleteMessage={deleteMessage}
        courseCode={sessionInfo?.courseCode}
        weekLabel={extractWeekNumber(sessionInfo?.title) ? `Week ${extractWeekNumber(sessionInfo?.title)}` : ''}
      />
    );
  }

  const courseLabel = sessionInfo
    ? [sessionInfo.courseCode, sessionInfo.courseName].filter(Boolean).join(' · ')
    : '';
  const weekNumber = extractWeekNumber(sessionInfo?.title);
  const weekLabel = weekNumber ? `Week ${weekNumber}` : '';
  const messageCount = questions.reduce((total, question) => total + 1 + (question.replies || []).length, 0);

  return (
    <div className={`${styles.card} ${fullPage ? styles.cardFull : ''}`}>
      {fullPage && (
        <div className={styles.windowBar}>
          <DashboardReturnLink className={styles.backLink}>
            <FiArrowLeft size={14} />
            Return to Dashboard
          </DashboardReturnLink>
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <h3 className={styles.title}>
            <FiMessageCircle size={18} />
            {fullPage && courseLabel ? courseLabel : (title || 'Session Q&A')}
            {isInstructor && !loading && (
              <span className={styles.questionCount}>
                · Total Messages: {messageCount}
              </span>
            )}
          </h3>
          {fullPage && (
            <div className={styles.subtitle}>
              Session Q&A{weekLabel ? ` · ${weekLabel}` : ''}
            </div>
          )}
        </div>
        {toast && (
          <div className={styles.toast} role="status" aria-live="polite" onClick={() => setToast(null)}>
            <FiMessageCircle size={14} />
            <span className={styles.toastText}>{toast}</span>
          </div>
        )}
        <div className={styles.headerActions}>
          <button type="button" className={styles.closeButton} onClick={() => setShowQr((v) => !v)} aria-label="Show QR code for mobile" aria-expanded={showQr} title="Open on phone (QR code)">
            <FiSmartphone size={18} />
          </button>
          {!fullPage && (
            <button type="button" className={styles.closeButton} onClick={popOut} aria-label="Open in separate window" title="Open in separate window">
              <FiExternalLink size={18} />
            </button>
          )}
        {onClose && (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
        )}
        </div>
      </div>
      {showQr && <QrPopover url={sessionQnaUrl(sessionId)} onHide={() => setShowQr(false)} />}

      <div className={styles.body}>
        {!loading && !isOpen && (
          <div className={styles.closedBanner}>
            Q&A is closed for this session. You can still review the thread below.
          </div>
        )}

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={fullPage ? styles.scrollAreaFull : styles.scrollArea}>
          {loading ? (
            <div className={styles.emptyState}>Loading questions...</div>
          ) : questions.length === 0 ? (
            <div className={styles.emptyState}>
              <FiMessageCircle size={20} />
              No questions yet{isOpen && !isInstructor ? ' — be the first to ask one.' : '.'}
            </div>
          ) : (
            <div className={styles.thread}>
              {questions.map((q) => (
                <div key={q.id} className={styles.questionCard}>
                  <div className={styles.messageMeta}>
                    <span className={`${styles.authorName} ${q.isAnonymous ? styles.authorNameAnonymous : ''}`}>
                      {q.authorName}
                    </span>
                    <span className={styles.timeStamp}>{formatTime(q.createdAt)}</span>
                  </div>
                  <ModeratedBody message={q} targetType="questions" canModerate={isInstructor} canEdit={!isInstructor && isOpen && q.isMine} onEdit={editMessage} onDelete={deleteMessage} >
                    <ReactionButtons
                    reactions={q.reactions}
                    myReaction={q.myReaction}
                    disabled={!isOpen}
                    onReact={(type) => toggleReaction('questions', q.id, type)}
                  />
                  </ModeratedBody>

                  {q.replies.length > 0 && (
                    <div className={styles.replyList}>
                      {q.replies.map((r) => (
                        <div key={r.id} className={styles.replyCard}>
                          <div className={styles.messageMeta}>
                            <span className={styles.authorName}>{r.authorName}</span>
                            {isInstructorReply(r) && <span className={styles.instructorBadge}>Instructor</span>}
                            <span className={styles.timeStamp}>{formatTime(r.createdAt)}</span>
                          </div>
                          <ModeratedBody message={r} targetType="replies" canModerate={isInstructor} onEdit={editMessage} onDelete={deleteMessage} >
                          <ReactionButtons
                            reactions={r.reactions}
                            myReaction={r.myReaction}
                            disabled={!isOpen}
                            onReact={(type) => toggleReaction('replies', r.id, type)}
                          />
                          </ModeratedBody>
                        </div>
                      ))}
                    </div>
                  )}

                  {isInstructor && isOpen && (
                    replyingTo === q.id ? (
                      <ReplyComposer
                        questionId={q.id}
                        posting={posting}
                        onSubmit={(questionId, body) => {
                          postReply(questionId, body);
                          setReplyingTo(null);
                        }}
                      />
                    ) : (
                      <button type="button" className={styles.replyToggle} onClick={() => setReplyingTo(q.id)}>
                        Reply
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {!isInstructor && isOpen && (
          <form
            className={styles.questionComposerWrap}
            onSubmit={(e) => {
              e.preventDefault();
              if (!questionDraft.trim()) return;
              postQuestion(questionDraft, askAnonymously);
              setQuestionDraft('');
            }}
          >
            <label className={styles.anonymousToggle}>
              <input
                type="checkbox"
                checked={askAnonymously}
                onChange={(e) => setAskAnonymously(e.target.checked)}
              />
              Post anonymously
            </label>
            <div className={styles.questionComposer}>
              <input
                type="text"
                className={styles.questionInput}
                placeholder="Ask a question..."
                dir="auto"
                value={questionDraft}
                onChange={(e) => setQuestionDraft(e.target.value)}
                maxLength={2000}
              />
              <button type="submit" className={styles.questionSendButton} disabled={posting || !questionDraft.trim()}>
                <FiSend size={16} />
                Ask
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SessionQnaPanel;
